import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackMiniAppEvent } from '../../utils/analytics';
import {
  getEngineRootStyle,
  titleStyle,
  instructionsStyle,
  primaryBtn,
  secondaryBtn,
  feedbackCorrect,
  feedbackIncorrect,
} from './shared/engineTheme';
import { useCelebration, CelebrationOverlay, A11yAnnouncement } from './shared/Celebration';
import { useGuidedHints } from './shared/useGuidedHints';
import GuidedHintOverlay, {
  GuidedHintHelpButton,
  guidedHintReadAloud,
} from './shared/GuidedHintOverlay';
import { GEAR_DIRECTION_ONE_SHOT } from './shared/engineInteractionOneShots';
import { isTapFirstLessonUi } from './shared/lessonUiMode';
import { shouldSuppressActivityTitle } from './shared/guidedPromptUtils';
import GearSvg from './shared/GearSvg';

/**
 * Gear train direction detective.
 *
 * Content:
 * {
 *   instructions?: string,
 *   prompt?: string,
 *   gears: [{ id, x, y, radius, teeth?, direction?, label? }],
 *   connections: [{ from: id, to: id }],
 *   targetRule: 'same_direction_as_starter' | 'opposite_direction_as_starter',
 *   starterGearId: string, // reference only — never part of the tap answer; pick *other* gears that match the rule
 *   scene?: { width?: number, height?: number },
 *   spinSpeed?: number,        // optional visual spin multiplier (default 1, clamped ~0.35–5); higher = faster rotation
 *   affirmationMs?: number,    // optional duration (ms) for one extra full turn on correct Check (default 720)
 *   meshConnectors?: boolean,  // default true: draw rim-to-rim lines instead of center lines (pitch-circle hint)
 *   victory?: { message?: string },
 *   explanation?: string,
 * }
 *
 * Guided lesson shell (`onRegisterLessonFooterActions`): **Check** and **Show answer** live in the lesson footer;
 * the gear train **spins only after Check** (or after Show answer). In-engine Check is hidden in that mode.
 *
 * Full involute tooth engagement (centers at pitch radii + tooth profile) needs layout math or an editor;
 * rim connectors + tooth phase offsets approximate “meshed” for kid demos without rewriting lesson coordinates.
 */

function buildAdjacency(connections) {
  const adj = new Map();
  const add = (a, b) => {
    if (!adj.has(a)) adj.set(a, []);
    if (!adj.has(b)) adj.set(b, []);
    adj.get(a).push(b);
    adj.get(b).push(a);
  };
  for (const e of connections || []) {
    if (e?.from && e?.to) add(e.from, e.to);
  }
  return adj;
}

function computeRotationSigns(gearIds, adj, starterId, starterSign) {
  const sign = new Map();
  if (!starterId || !adj.has(starterId)) return sign;
  sign.set(starterId, starterSign >= 0 ? 1 : -1);
  const q = [starterId];
  while (q.length) {
    const u = q.shift();
    const su = sign.get(u);
    for (const v of adj.get(u) || []) {
      if (!sign.has(v)) {
        sign.set(v, -su);
        q.push(v);
      }
    }
  }
  return sign;
}

function setsEqual(a, b) {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

function easeOutCubic(u) {
  const t = clamp(u, 0, 1);
  return 1 - (1 - t) ** 3;
}

function rimLineEndpoints(a, b) {
  const ra = Math.max(18, Number(a.radius) || 36);
  const rb = Math.max(18, Number(b.radius) || 36);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  return {
    x1: a.x + ux * ra,
    y1: a.y + uy * ra,
    x2: b.x - ux * rb,
    y2: b.y - uy * rb,
  };
}

function computeChainToothPhases(gears, adj, starterId) {
  const phases = {};
  const gById = Object.fromEntries(gears.map((g) => [g.id, g]));
  if (!starterId || !gById[starterId] || !adj.has(starterId)) return phases;
  phases[starterId] = Number(gById[starterId].meshPhaseDeg) || 0;
  const q = [starterId];
  while (q.length) {
    const u = q.shift();
    for (const v of adj.get(u) || []) {
      if (phases[v] !== undefined) continue;
      const gv = gById[v];
      if (!gv) continue;
      const tv = Math.max(6, Math.round(Number(gv?.teeth) || 12));
      const authored = gv.meshPhaseDeg;
      if (authored != null && Number.isFinite(Number(authored))) {
        phases[v] = Number(authored);
      } else {
        phases[v] = phases[u] + 180 / tv;
      }
      q.push(v);
    }
  }
  return phases;
}

export default function GearDirectionEngine({
  title = 'Gear directions',
  content = {},
  hints = [],
  explanation,
  activityId = 'engine_gear_direction',
  onProgress,
  onComplete,
  guidedHintsEnabled = true,
  lessonUiMode = 'default',
  guidedContext,
  onRegisterLessonFooterActions,
}) {
  const singleSurface = guidedContext?.singleSurface === true;
  const stepTitle = typeof guidedContext?.stepTitle === 'string' ? guidedContext.stepTitle : '';
  const suppressActivityTitle = shouldSuppressActivityTitle({ stepTitle, activityTitle: title });
  const tapFirst = isTapFirstLessonUi(lessonUiMode);
  const compactGear = tapFirst && singleSurface;
  const shellLesson = typeof onRegisterLessonFooterActions === 'function';

  const gears = Array.isArray(content.gears) ? content.gears : [];
  const connections = content.connections || [];
  const starterGearId = content.starterGearId || gears[0]?.id;
  const targetRule = content.targetRule || 'same_direction_as_starter';
  const sceneW = Number(content.scene?.width) || 320;
  const sceneH = Number(content.scene?.height) || 220;

  const starterGear = useMemo(
    () => gears.find((g) => g.id === starterGearId) || gears[0],
    [gears, starterGearId],
  );

  const starterSign = starterGear?.direction != null ? (Number(starterGear.direction) >= 0 ? 1 : -1) : 1;

  const adj = useMemo(() => buildAdjacency(connections), [connections]);
  const signs = useMemo(
    () => computeRotationSigns(gears.map((g) => g.id), adj, starterGear?.id, starterSign),
    [gears, adj, starterGear?.id, starterSign],
  );

  const spinSpeedMul = useMemo(() => {
    const raw = Number(content.spinSpeed);
    if (!Number.isFinite(raw) || raw <= 0) return 1;
    return clamp(raw, 0.35, 5);
  }, [content.spinSpeed]);

  const toothPhases = useMemo(
    () => computeChainToothPhases(gears, adj, starterGear?.id),
    [gears, adj, starterGear?.id],
  );

  const meshConnectors = content.meshConnectors !== false;

  const correctIds = useMemo(() => {
    const out = new Set();
    const sid = starterGear?.id;
    const ss = signs.get(sid) ?? 1;
    for (const g of gears) {
      if (g.id === sid) continue;
      const s = signs.get(g.id);
      if (s == null) continue;
      const wantSame = targetRule === 'same_direction_as_starter';
      const ok = wantSame ? s === ss : s === -ss;
      if (ok) out.add(g.id);
    }
    return out;
  }, [gears, signs, starterGear?.id, targetRule]);

  const [selected, setSelected] = useState(() => new Set());
  const [checkedWrong, setCheckedWrong] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [showCelebration, celebrate] = useCelebration();
  const [a11y, setA11y] = useState('');
  const angleRef = useRef({});
  const lastTickRef = useRef(0);
  const gearRefs = useRef({});
  const affirmRef = useRef({ active: false, t0: 0, duration: 720 });
  const affirmFreezeRef = useRef({});

  const getExplanation = useCallback(() => content.explanation, [content.explanation]);

  const guided = useGuidedHints({
    activityId,
    hints,
    oneShotInteractionHint: GEAR_DIRECTION_ONE_SHOT,
    explanation,
    getExplanation,
    isActivityComplete: completed,
    enabled: guidedHintsEnabled,
  });
  const { noteUserAction, noteIncorrectAnswer, noteCorrectAnswer } = guided;
  const narrowGear = compactGear && guided.isNarrow;

  const trainSpinningRef = useRef(false);

  const snapGearsToRest = useCallback(() => {
    gears.forEach((g) => {
      angleRef.current[g.id] = 0;
      const el = gearRefs.current[g.id];
      if (el) el.style.transform = 'rotate(0deg)';
    });
  }, [gears]);

  useEffect(() => {
    gears.forEach((g) => {
      angleRef.current[g.id] = 0;
    });
    affirmRef.current.active = false;
    trainSpinningRef.current = false;
    const id = requestAnimationFrame(() => snapGearsToRest());
    return () => cancelAnimationFrame(id);
  }, [gears, snapGearsToRest]);

  useEffect(() => {
    let raf;
    const tick = (t) => {
      if (!lastTickRef.current) lastTickRef.current = t;
      const dt = clamp((t - lastTickRef.current) / 1000, 0, 0.08);
      lastTickRef.current = t;
      const base = 400 * spinSpeedMul;

      const affirm = affirmRef.current;
      if (affirm.active) {
        const dur = affirm.duration || 720;
        const u = clamp((t - affirm.t0) / dur, 0, 1);
        if (u < 1) {
          const theta = easeOutCubic(u) * 360;
          for (const g of gears) {
            const sRaw = signs.get(g.id) ?? 1;
            const sg = sRaw === 0 ? 1 : Math.sign(sRaw);
            const fr = affirmFreezeRef.current[g.id] ?? 0;
            const display = fr + theta * sg;
            const el = gearRefs.current[g.id];
            if (el) el.style.transform = `rotate(${display.toFixed(2)}deg)`;
          }
          raf = requestAnimationFrame(tick);
          return;
        }
        for (const g of gears) {
          const sRaw = signs.get(g.id) ?? 1;
          const sg = sRaw === 0 ? 1 : Math.sign(sRaw);
          const fr = affirmFreezeRef.current[g.id] ?? 0;
          angleRef.current[g.id] = fr + 360 * sg;
        }
        affirm.active = false;
      }

      if (!trainSpinningRef.current) {
        raf = requestAnimationFrame(tick);
        return;
      }

      for (const g of gears) {
        const s = signs.get(g.id) ?? 0;
        const r = Math.max(16, Number(g.radius) || 32);
        const w = (s * base) / r;
        angleRef.current[g.id] = (angleRef.current[g.id] || 0) + w * dt;
        const el = gearRefs.current[g.id];
        if (el) el.style.transform = `rotate(${angleRef.current[g.id].toFixed(2)}deg)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      lastTickRef.current = 0;
      cancelAnimationFrame(raf);
    };
  }, [gears, signs, spinSpeedMul]);

  const revealAnswer = useCallback(() => {
    if (completed) return;
    noteUserAction();
    trainSpinningRef.current = true;
    setSelected(new Set(correctIds));
    setCheckedWrong(false);
  }, [completed, noteUserAction, correctIds]);

  const checkAnswer = useCallback(() => {
    if (completed) return;
    trainSpinningRef.current = true;
    noteUserAction();
    const sid = starterGear?.id;
    const answerSet = new Set([...selected].filter((id) => id && id !== sid));
    const ok = setsEqual(answerSet, correctIds);
    if (ok) {
      affirmFreezeRef.current = {};
      for (const g of gears) {
        affirmFreezeRef.current[g.id] = angleRef.current[g.id] || 0;
      }
      const ms = Number(content.affirmationMs);
      const duration = Number.isFinite(ms) && ms > 0 ? clamp(ms, 400, 2200) : 720;
      affirmRef.current = { active: true, t0: performance.now(), duration };
      setCompleted(true);
      noteCorrectAnswer();
      celebrate();
      setA11y('That matches the spin direction rule.');
      trackMiniAppEvent.interaction(activityId, 'correct');
      trackMiniAppEvent.completed(activityId);
      onComplete?.({ activityId, engine: 'gear_direction', perfect: true });
    } else {
      setCheckedWrong(true);
      noteIncorrectAnswer();
      setA11y('Not quite — try another combo, or tap Hint.');
      trackMiniAppEvent.interaction(activityId, 'incorrect');
    }
  }, [
    completed,
    noteUserAction,
    starterGear?.id,
    selected,
    correctIds,
    gears,
    content.affirmationMs,
    noteCorrectAnswer,
    celebrate,
    activityId,
    onComplete,
    noteIncorrectAnswer,
  ]);

  useEffect(() => {
    if (!onRegisterLessonFooterActions) return undefined;
    if (completed || gears.length === 0) {
      onRegisterLessonFooterActions(null);
      return undefined;
    }
    onRegisterLessonFooterActions({
      checkAnswer,
      checkAnswerDisabled: false,
      showAnswer: revealAnswer,
    });
    return () => onRegisterLessonFooterActions(null);
  }, [onRegisterLessonFooterActions, completed, gears.length, checkAnswer, revealAnswer]);

  const toggleGear = useCallback(
    (id) => {
      if (completed) return;
      if (id === starterGear?.id) return;
      if (trainSpinningRef.current) {
        trainSpinningRef.current = false;
        snapGearsToRest();
      }
      noteUserAction();
      setCheckedWrong(false);
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      onProgress?.({ activityId, engine: 'gear_direction', phase: 'toggle', gearId: id });
    },
    [completed, starterGear?.id, noteUserAction, snapGearsToRest, activityId, onProgress],
  );

  const tryAgain = useCallback(() => {
    if (completed) return;
    trainSpinningRef.current = false;
    snapGearsToRest();
    noteUserAction();
    setSelected(new Set());
    setCheckedWrong(false);
  }, [completed, snapGearsToRest, noteUserAction]);

  const prompt =
    content.prompt ||
    (targetRule === 'opposite_direction_as_starter'
      ? 'Tap every other gear that spins the opposite way from the starter.'
      : 'Tap every other gear that spins the same way as the starter (the Start gear is your reference only).');

  return (
    <div
      style={{
        ...getEngineRootStyle({ singleSurface }),
        position: 'relative',
        ...(guided.contentReserveStyle || {}),
        ...(compactGear ? { minHeight: 0, display: 'flex', flexDirection: 'column', flex: 1, gap: 8 } : {}),
      }}
    >
      <CelebrationOverlay visible={showCelebration} message={content.victory?.message || 'You traced the spin!'} />
      <A11yAnnouncement message={a11y} />
      <GuidedHintOverlay
        open={guided.open}
        isNarrow={guided.isNarrow}
        visibleLevel={guided.visibleLevel}
        maxLevel={guided.maxLevel}
        isInteractionPhase={guided.isInteractionPhase}
        anotherClueEnabled={guided.anotherClueEnabled}
        hintEntry={guided.hintEntry}
        showHowAvailable={guided.showHowAvailable}
        resolvedExplanation={guided.resolvedExplanation}
        explanationOpen={guided.explanationOpen}
        onDismiss={guided.dismissOverlay}
        onAnotherClue={guided.anotherClue}
        onReadAloud={() =>
          guidedHintReadAloud(guided.hintEntry, activityId, guided.readAloudTrackLevel)
        }
        onShowHow={guided.openExplanation}
      />
      <GuidedHintHelpButton visible={guided.showHelpButton} onClick={guided.requestHintManually} />

      {!suppressActivityTitle ? (
        <h2 style={{ ...titleStyle, margin: compactGear ? '0 0 4px' : titleStyle.margin }}>{title}</h2>
      ) : null}
      {compactGear ? (
        <p
          style={{
            ...instructionsStyle,
            fontSize: narrowGear ? 13 : 14,
            lineHeight: 1.35,
            margin: 0,
            fontWeight: 600,
          }}
        >
          {prompt}
        </p>
      ) : (
        <>
          {content.instructions && <p style={instructionsStyle}>{content.instructions}</p>}
          <p style={{ ...instructionsStyle, fontWeight: 700, marginTop: content.instructions ? undefined : 0 }}>
            {prompt}
          </p>
        </>
      )}

      {compactGear ? (
        // FIX 1+2: flex row with responsive items for lesson shell mobile layout
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 80,
            borderRadius: 12,
            border: '1px solid var(--color-border)',
            background: 'var(--color-bg)',
            gap: 6,
            padding: 8,
            boxSizing: 'border-box',
          }}
        >
          {gears.map((g) => {
            const r = Math.max(18, Number(g.radius) || 36);
            const isStarter = g.id === starterGear?.id;
            const active = selected.has(g.id);
            const teeth = g.teeth != null ? Number(g.teeth) : Math.max(8, Math.round(r / 2.5));
            const gearWidth = `calc(${(100 / gears.length).toFixed(1)}% - 6px)`;
            const itemStyle = {
              width: gearWidth,
              aspectRatio: '1',
              minWidth: 44,
              maxWidth: 72,
              padding: 4,
              borderRadius: 12,
              border: `2px solid ${isStarter ? 'var(--color-primary)' : active ? '#22c55e' : 'var(--color-border)'}`,
              background: active ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.04)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
              flexShrink: 0,
            };
            const innerGear = (
              <>
                <div
                  ref={(el) => { gearRefs.current[g.id] = el; }}
                  style={{ width: '60%', aspectRatio: '1' }}
                >
                  <GearSvg
                    teeth={teeth}
                    size={80}
                    color={isStarter ? 'var(--color-primary)' : '#64748b'}
                    accent="#fff"
                    ariaLabel={g.label || `Gear ${g.id}`}
                    phaseDeg={toothPhases[g.id] ?? 0}
                  />
                </div>
                <span style={{ fontSize: 10, fontWeight: 800, marginTop: 2, color: 'var(--color-text)' }}>
                  {isStarter ? 'Start' : g.label || g.id}
                </span>
              </>
            );
            if (isStarter) {
              return (
                <div
                  key={g.id}
                  role="img"
                  aria-label="Starter gear — direction reference"
                  style={{ ...itemStyle, cursor: 'default', opacity: completed ? 0.95 : 1 }}
                >
                  {innerGear}
                </div>
              );
            }
            return (
              <button
                key={g.id}
                type="button"
                disabled={completed}
                onClick={() => toggleGear(g.id)}
                aria-pressed={active}
                style={{ ...itemStyle, cursor: completed ? 'default' : 'pointer' }}
              >
                {innerGear}
              </button>
            );
          })}
        </div>
      ) : (
        // Original SVG scene for desktop / standalone mode
        <div
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: `${sceneW} / ${sceneH}`,
            borderRadius: 16,
            border: '1px solid var(--color-border)',
            background: 'var(--color-bg)',
            marginBottom: 12,
            overflow: 'hidden',
          }}
        >
          <svg
            viewBox={`0 0 ${sceneW} ${sceneH}`}
            width="100%"
            height="100%"
            style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
            aria-hidden
          >
            {(connections || []).map((e, i) => {
              const a = gears.find((g) => g.id === e.from);
              const b = gears.find((g) => g.id === e.to);
              if (!a || !b) return null;
              if (meshConnectors) {
                const { x1, y1, x2, y2 } = rimLineEndpoints(a, b);
                return (
                  <line
                    key={`${e.from}-${e.to}-${i}`}
                    x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke="var(--color-border)" strokeWidth={3} strokeLinecap="round" opacity={0.85}
                  />
                );
              }
              return (
                <line
                  key={`${e.from}-${e.to}-${i}`}
                  x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                  stroke="var(--color-border)" strokeWidth={3} strokeLinecap="round" opacity={0.85}
                />
              );
            })}
          </svg>
          {gears.map((g) => {
            const r = Math.max(18, Number(g.radius) || 36);
            const size = r * 2;
            const isStarter = g.id === starterGear?.id;
            const active = selected.has(g.id);
            const teeth = g.teeth != null ? Number(g.teeth) : Math.max(8, Math.round(r / 2.5));
            const boxStyle = {
              position: 'absolute',
              left: `${(g.x / sceneW) * 100}%`,
              top: `${(g.y / sceneH) * 100}%`,
              transform: 'translate(-50%, -50%)',
              width: size + 16,
              height: size + 16,
              minWidth: 52,
              minHeight: 52,
              padding: 6,
              borderRadius: 16,
              border: `3px solid ${isStarter ? 'var(--color-primary)' : active ? '#22c55e' : 'var(--color-border)'}`,
              background: active ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.04)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
            };
            const inner = (
              <>
                <div ref={(el) => { gearRefs.current[g.id] = el; }} style={{ width: size, maxWidth: '100%' }}>
                  <GearSvg
                    teeth={teeth}
                    size={size}
                    color={isStarter ? 'var(--color-primary)' : '#64748b'}
                    accent="#fff"
                    ariaLabel={g.label || `Gear ${g.id}`}
                    phaseDeg={toothPhases[g.id] ?? 0}
                  />
                </div>
                <span style={{ fontSize: 11, fontWeight: 800, marginTop: 2, color: 'var(--color-text)' }}>
                  {isStarter ? 'Start' : g.label || g.id}
                </span>
              </>
            );
            if (isStarter) {
              return (
                <div
                  key={g.id}
                  role="img"
                  aria-label="Starter gear — direction reference. Tap other gears, then Check to see the train spin."
                  style={{ ...boxStyle, cursor: 'default', opacity: completed ? 0.95 : 1 }}
                >
                  {inner}
                </div>
              );
            }
            return (
              <button
                key={g.id}
                type="button"
                disabled={completed}
                onClick={() => toggleGear(g.id)}
                aria-pressed={active}
                style={{ ...boxStyle, cursor: completed ? 'default' : 'pointer' }}
              >
                {inner}
              </button>
            );
          })}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: compactGear ? 6 : 8,
        }}
      >
        {!shellLesson ? (
          <button
            type="button"
            style={{ ...primaryBtn, flex: '1 1 140px', minHeight: compactGear ? 44 : 48 }}
            onClick={checkAnswer}
            disabled={completed}
          >
            Check
          </button>
        ) : null}
        <button
          type="button"
          style={{
            ...secondaryBtn,
            flex: shellLesson ? '1 1 100%' : '1 1 120px',
            width: shellLesson ? '100%' : undefined,
            minHeight: compactGear ? 44 : 48,
            fontSize: compactGear ? 14 : 16,
          }}
          onClick={tryAgain}
          disabled={completed}
        >
          Clear picks
        </button>
      </div>

      {checkedWrong && !completed ? (
        <div
          style={{
            ...feedbackIncorrect,
            ...(compactGear
              ? {
                  background: '#FAEEDA',
                  borderRadius: 'var(--border-radius-md, 10px)',
                  padding: '8px 12px',
                  fontSize: 12,
                  lineHeight: 1.4,
                  marginTop: 0,
                  width: '100%',
                  boxSizing: 'border-box',
                }
              : { marginTop: 10 }),
          }}
        >
          Not quite — try again. Meshing neighbors spin opposite ways.
        </div>
      ) : null}
      {completed ? (
        <div
          style={{
            ...feedbackCorrect,
            ...(compactGear
              ? {
                  background: '#EAF3DE',
                  borderRadius: 'var(--border-radius-md, 10px)',
                  padding: '8px 12px',
                  fontSize: 12,
                  marginTop: 0,
                  width: '100%',
                  boxSizing: 'border-box',
                }
              : { marginTop: 10 }),
          }}
        >
          {content.victory?.message || 'Yes — those gears match the rule!'}
        </div>
      ) : null}
    </div>
  );
}
